import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaFileUpload, FaFilter, FaCalendar, FaListOl, FaQuestionCircle } from 'react-icons/fa';
import { FiList } from 'react-icons/fi';

interface MBSItem {
  id: number;
  item_number: string;
  description: string;
  category: string;
  schedule_fee: number;
  benefit_100: number;
  effective_date: string;
  end_date?: string;
  is_active: boolean;
  item_type?: string;
  provider_type?: string;
}

const MBSItems: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<MBSItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [effectiveDateFilter, setEffectiveDateFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [categories, setCategories] = useState<string[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState(0);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchMBSItems();
    fetchStatistics();
  }, [currentPage, searchTerm, categoryFilter, activeFilter]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, activeFilter]);

  const fetchCategories = async () => {
    try {
      if (!token) {
        return;
      }

      const response = await apiCall('/billing/mbs-items/categories/', { method: 'GET' }, token);
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      if (!token) {
        return;
      }

      // Build query parameters (same filters as main list)
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (categoryFilter) {
        params.append('category', categoryFilter);
      }

      const response = await apiCall(`/billing/mbs-items/statistics/?${params}`, { method: 'GET' }, token);
      
      if (response.ok) {
        const data = await response.json();
        setTotalCount(data.total || 0);
        setActiveCount(data.active || 0);
        setInactiveCount(data.inactive || 0);
        setCategoryCount(data.categories || 0);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchMBSItems = async () => {
    setLoading(true);
    try {
      if (!token) {
        setLoading(false);
        return;
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('page_size', itemsPerPage.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (categoryFilter) {
        params.append('category', categoryFilter);
      }
      if (activeFilter === 'active') {
        params.append('is_active', 'true');
      } else if (activeFilter === 'inactive') {
        params.append('is_active', 'false');
      }
      // 'all' means no is_active filter

      const response = await apiCall(`/billing/mbs-items/?${params}`, { method: 'GET' }, token);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle paginated response
      if (data.results) {
        setItems(data.results);
        setTotalCount(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / itemsPerPage));
      } else {
        setItems(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
        setTotalPages(1);
      }
      
      if (data.results && data.results.length === 0) {
        console.info('No MBS items found in database. Upload an MBS schedule to populate data.');
      }
    } catch (error) {
      console.error('Error fetching MBS items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Remove client-side filtering since backend handles it
  const filteredItems = items;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  return (
    <>
      <style>{`
        .tooltip-rounded::before {
          border-radius: 0.5rem;
          z-index: 99999 !important;
        }
        .tooltip-rounded::after {
          z-index: 99999 !important;
        }
        .tooltip-rounded {
          z-index: 99999 !important;
          position: relative;
        }
      `}</style>
      <div className="p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                <FaListOl className="text-primary" />
                MBS Items
                <div className="tooltip tooltip-rounded tooltip-right" data-tip="MBS items are tracked by effective date. When a procedure is performed, the system uses the schedule rate that was effective on the date of service. Historical rates are maintained for billing delayed procedures.">
                  <FaQuestionCircle className="text-base-content opacity-40 text-lg cursor-help" />
                </div>
              </h1>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <FiList className="text-primary text-2xl" />
              <span className="text-xs">Total Items:</span>
              <span className="text-lg font-bold">{totalCount}</span>
            </div>
            <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-xs">Active:</span>
              <span className="text-lg font-bold text-success">{activeCount}</span>
            </div>
            <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-xs">Categories:</span>
              <span className="text-lg font-bold">{categoryCount}</span>
            </div>
            <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-xs">Historical:</span>
              <span className="text-lg font-bold">{inactiveCount}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/billing/mbs-items/upload')}
              className="btn btn-outline gap-2"
            >
              <FaFileUpload />
              Upload MBS Schedule
            </button>
            <button
              onClick={() => navigate('/billing/mbs-items/new')}
              className="btn btn-primary gap-2"
            >
              <FaPlus />
              Add MBS Item
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-base-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-6">
            {/* Search */}
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm font-medium whitespace-nowrap">Search</span>
              <input
                type="text"
                placeholder="Search by item number or description..."
                className="input input-bordered w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Category</span>
              <select
                className="select select-bordered"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Active Status Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Status</span>
              <select
                className="select select-bordered"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <option value="active">Active Items</option>
                <option value="inactive">Inactive/Historical</option>
                <option value="all">All Items</option>
              </select>
            </div>

            {/* Effective Date Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Effective Date</span>
              <input
                type="month"
                className="input input-bordered"
                value={effectiveDateFilter}
                onChange={(e) => setEffectiveDateFilter(e.target.value)}
                placeholder="Effective Date"
              />
            </div>
          </div>
        </div>

        {/* MBS Items Table */}
        <div className="bg-base-100 rounded-lg shadow overflow-hidden">
          <div className="card-body p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <FaSearch className="mx-auto text-4xl mb-4" />
                <p>No MBS items found matching your criteria</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Item Number</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Schedule Fee</th>
                        <th>Benefit (100%)</th>
                        <th>Effective Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item) => (
                        <tr key={item.id} className={!item.is_active ? 'opacity-50' : ''}>
                          <td className="font-bold">{item.item_number}</td>
                          <td className="max-w-md">
                            <div className="truncate" title={item.description}>
                              {item.description}
                            </div>
                          </td>
                          <td>
                            <div className="badge badge-outline badge-sm">
                              {item.category}
                            </div>
                          </td>
                          <td className="font-semibold">{formatCurrency(item.schedule_fee)}</td>
                          <td>{formatCurrency(item.benefit_100)}</td>
                          <td>
                            <div className="flex items-center gap-1 text-xs">
                              <FaCalendar className="text-xs" />
                              {moment(item.effective_date).format('DD/MM/YYYY')}
                            </div>
                          </td>
                          <td>
                            {item.is_active ? (
                              <div className="badge badge-success badge-sm">Active</div>
                            ) : (
                              <div className="badge badge-ghost badge-sm">Inactive</div>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <button
                                onClick={() => navigate(`/billing/mbs-items/${item.id}`)}
                                className="btn btn-ghost btn-xs"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this item?')) {
                                    // Handle delete
                                  }
                                }}
                                className="btn btn-ghost btn-xs text-error"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center p-4">
                    <div className="join">
                      <button
                        className="join-item btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      >
                        «
                      </button>
                      <button className="join-item btn">
                        Page {currentPage} of {totalPages} ({totalCount} total items)
                      </button>
                      <button
                        className="join-item btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default MBSItems;
